import React, { useState } from 'react';
import {
  Briefcase,
  Clock,
  CreditCard,
  CheckCircle2,
  Check,
  RotateCcw,
  Sparkles,
  UserCheck
} from 'lucide-react';
import {
  SAMPLE_CANDIDATES,
  SAMPLE_TIMESHEET,
  SAMPLE_ONBOARDING_TASKS
} from '../data/demoContent';

const STAGES = ['Applied', 'Screening', 'Interviewing', 'Client Offer', 'Placed'];

const InteractiveSandbox = () => {
  const [activeTab, setActiveTab] = useState('ats');

  // Sandbox 1: ATS Candidate Pipeline state
  const [candidates, setCandidates] = useState(SAMPLE_CANDIDATES);

  const handleStageChange = (candId, newStage) => {
    setCandidates((prev) =>
      prev.map((c) => (c.id === candId ? { ...c, stage: newStage } : c))
    );
  };

  // Sandbox 2: Timesheet state
  const [timesheetStatus, setTimesheetStatus] = useState('pending');
  const [approvedTimestamp, setApprovedTimestamp] = useState(null);

  const handleApproveTimesheet = () => {
    setTimesheetStatus('approved');
    setApprovedTimestamp(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  };

  const handleResetTimesheet = () => {
    setTimesheetStatus('pending');
    setApprovedTimestamp(null);
  };

  // Sandbox 3: Invoice Calculator state
  const [hoursBilled, setHoursBilled] = useState(40);
  const [hourlyRate, setHourlyRate] = useState(95);
  const taxRate = 0.08;
  const subtotal = hoursBilled * hourlyRate;
  const taxes = subtotal * taxRate;
  const grandTotal = subtotal + taxes;

  // Sandbox 4: Onboarding Tasks state
  const [tasks, setTasks] = useState(SAMPLE_ONBOARDING_TASKS);

  const toggleTask = (taskId) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          return {
            ...t,
            status: t.status === 'completed' ? 'pending' : 'completed'
          };
        }
        return t;
      })
    );
  };

  const completedCount = tasks.filter((t) => t.status === 'completed').length;
  const onboardingPercentage = Math.round((completedCount / tasks.length) * 100);

  return (
    <section className="erp-sandbox-section-white" id="sandbox">
      <div className="jp-workspace-container">
        <div className="erp-clean-header">
          <div className="erp-clean-badge">
            <Sparkles size={13} />
            <span>Interactive Simulator</span>
          </div>
          <h2 className="erp-clean-title">
            Test-Drive the ERP in Real Time
          </h2>
          <p className="erp-clean-desc">
            Experience how intuitive the workflows are. Advance a candidate through the pipeline, approve a weekly timesheet, or compute an invoice.
          </p>
        </div>

        {/* Simulator Switcher Tabs (Clean White Buttons) */}
        <div className="erp-sandbox-tabs-white">
          <button
            type="button"
            className={`erp-sandbox-tab-white ${activeTab === 'ats' ? 'active' : ''}`}
            onClick={() => setActiveTab('ats')}
          >
            <Briefcase size={15} />
            <span>ATS Candidate Board</span>
          </button>

          <button
            type="button"
            className={`erp-sandbox-tab-white ${activeTab === 'timesheet' ? 'active' : ''}`}
            onClick={() => setActiveTab('timesheet')}
          >
            <Clock size={15} />
            <span>1-Click Timesheet Approval</span>
          </button>

          <button
            type="button"
            className={`erp-sandbox-tab-white ${activeTab === 'invoice' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoice')}
          >
            <CreditCard size={15} />
            <span>Automated Billing Calculator</span>
          </button>

          <button
            type="button"
            className={`erp-sandbox-tab-white ${activeTab === 'onboarding' ? 'active' : ''}`}
            onClick={() => setActiveTab('onboarding')}
          >
            <UserCheck size={15} />
            <span>Onboarding Checklist Drawer</span>
          </button>
        </div>

        {/* White Surface Workstation */}
        <div className="erp-sandbox-frame-white">
          {/* SIMULATOR 1: ATS PIPELINE */}
          {activeTab === 'ats' && (
            <div>
              <div className="erp-sandbox-tab-header">
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                    Active Candidate Pipeline: Cloud & DevOps
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    Change any candidate's recruitment stage to watch their profile update live.
                  </p>
                </div>
                <button
                  type="button"
                  style={{ background: '#ffffff', border: '1.5px solid #cbd5e1', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setCandidates(SAMPLE_CANDIDATES)}
                >
                  <RotateCcw size={13} />
                  <span>Reset Data</span>
                </button>
              </div>

              <div className="erp-sandbox-candidate-grid">
                {candidates.map((cand) => (
                  <div key={cand.id} className="erp-candidate-card-white">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: '800', color: '#0f172a' }}>{cand.name}</h4>
                        <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600' }}>{cand.role}</div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0' }}>
                        {cand.score} Match
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                      <span>Exp: <strong style={{ color: '#0f172a' }}>{cand.exp}</strong></span>
                      <span>Rate: <strong style={{ color: '#2563eb' }}>{cand.rate}</strong></span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                      {cand.skills.map((s, idx) => (
                        <span key={idx} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' }}>
                          {s}
                        </span>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Stage:</span>
                      <select
                        value={cand.stage}
                        onChange={(e) => handleStageChange(cand.id, e.target.value)}
                        style={{ background: '#ffffff', border: '1.5px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: '#0f172a', cursor: 'pointer' }}
                      >
                        {STAGES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SIMULATOR 2: 1-CLICK TIMESHEET APPROVAL */}
          {activeTab === 'timesheet' && (
            <div className="erp-timesheet-card-white">
              <div className="erp-sandbox-tab-header">
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Weekly Timesheet Review</h3>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                    Consultant: <strong style={{ color: '#0f172a' }}>{SAMPLE_TIMESHEET.consultant}</strong> • Client: <strong style={{ color: '#2563eb' }}>{SAMPLE_TIMESHEET.client}</strong>
                  </div>
                </div>

                {timesheetStatus === 'approved' && (
                  <button
                    type="button"
                    style={{ background: '#ffffff', border: '1.5px solid #cbd5e1', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    onClick={handleResetTimesheet}
                  >
                    Reset Demo
                  </button>
                )}
              </div>

              <div className="erp-sandbox-timesheet-grid">
                {SAMPLE_TIMESHEET.days.map((d) => (
                  <div key={d.day} className="erp-day-card-white">
                    <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700' }}>{d.day}</div>
                    <div style={{ fontSize: '18px', fontWeight: '850', color: '#0f172a', marginTop: '2px' }}>{d.hours.toFixed(1)}h</div>
                    <div style={{ fontSize: '11px', color: '#059669', fontWeight: '600' }}>Regular</div>
                  </div>
                ))}
              </div>

              <div className="erp-sandbox-timesheet-footer">
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Total Approved Hours:</div>
                  <div style={{ fontSize: '22px', fontWeight: '850', color: '#0f172a' }}>40.0 Hours</div>
                  <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600' }}>Bill Rate: $95.00/hr ($3,800.00 Total)</div>
                </div>

                {timesheetStatus === 'pending' ? (
                  <button
                    type="button"
                    className="erp-demo-btn-primary"
                    onClick={handleApproveTimesheet}
                    style={{ padding: '10px 22px', fontSize: '13.5px' }}
                  >
                    <Check size={16} />
                    <span>Approve Timesheet (1-Click)</span>
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ecfdf5', border: '1.5px solid #a7f3d0', padding: '8px 18px', borderRadius: '24px' }}>
                    <CheckCircle2 size={18} color="#059669" />
                    <div>
                      <div style={{ color: '#059669', fontWeight: '700', fontSize: '13px' }}>Approved by Manager</div>
                      <div style={{ color: '#64748b', fontSize: '11px' }}>Stamp: Today at {approvedTimestamp} • Sent to Billing</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SIMULATOR 3: INVOICE GENERATOR */}
          {activeTab === 'invoice' && (
            <div className="erp-sandbox-invoice-grid">
              <div style={{ background: '#ffffff', padding: '22px', borderRadius: '12px', border: '1.5px solid #cbd5e1' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>Adjust Billing Parameters</h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 20px 0' }}>
                  Move the sliders to see the client-ready invoice update immediately.
                </p>

                <div style={{ marginBottom: '18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    <span>Billable Hours:</span>
                    <span style={{ color: '#2563eb', fontWeight: '700' }}>{hoursBilled} hrs</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="60"
                    step="1"
                    value={hoursBilled}
                    onChange={(e) => setHoursBilled(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    <span>Hourly Bill Rate:</span>
                    <span style={{ color: '#059669', fontWeight: '700' }}>${hourlyRate}/hr</span>
                  </div>
                  <input
                    type="range"
                    min="60"
                    max="160"
                    step="5"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#059669', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ padding: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', color: '#1e40af' }}>
                  ✓ In Prophecy Nexus, approving timesheets automatically creates this invoice without manual calculation.
                </div>
              </div>

              {/* White Paper Invoice */}
              <div className="erp-paper-invoice-white">
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px', marginBottom: '14px' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '850', color: '#0f172a' }}>PROPHECY TECHNOLOGIES</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Workforce Solutions LLC</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a' }}>INVOICE #INV-2026-104</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Date: Today</div>
                  </div>
                </div>

                <div style={{ fontSize: '12px', color: '#475569', marginBottom: '14px' }}>
                  <div><strong>Billed To:</strong> Citigroup Enterprise Systems</div>
                  <div><strong>Consultant:</strong> Sarah Jenkins (React Lead)</div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#334155', marginBottom: '6px' }}>
                  <span>Consulting Services ({hoursBilled} hrs @ ${hourlyRate}/hr):</span>
                  <strong>${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>
                  <span>Taxes (8%):</span>
                  <span>${taxes.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '850', color: '#0f172a', borderTop: '2px solid #cbd5e1', paddingTop: '10px', marginTop: '10px' }}>
                  <span>Total Amount Due:</span>
                  <span style={{ color: '#2563eb' }}>${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          )}

          {/* SIMULATOR 4: ONBOARDING DRAWER */}
          {activeTab === 'onboarding' && (
            <div style={{ maxWidth: '720px', margin: '0 auto' }}>
              <div className="erp-sandbox-tab-header">
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Consultant Readiness Checklist</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    Click tasks to toggle completion status.
                  </p>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Readiness Score: </span>
                  <strong style={{ fontSize: '18px', color: '#059669' }}>{onboardingPercentage}%</strong>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
                <div
                  style={{
                    width: `${onboardingPercentage}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #2563eb, #10b981)',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {tasks.map((task) => {
                  const isCompleted = task.status === 'completed';
                  return (
                    <div
                      key={task.id}
                      onClick={() => toggleTask(task.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#ffffff',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '14px 18px',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            border: isCompleted ? 'none' : '2px solid #94a3b8',
                            background: isCompleted ? '#059669' : '#ffffff',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {isCompleted && <Check size={14} />}
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: isCompleted ? '#64748b' : '#0f172a', textDecoration: isCompleted ? 'line-through' : 'none' }}>
                          {task.title}
                        </span>
                      </div>

                      <span style={{ fontSize: '12px', fontWeight: '700', color: isCompleted ? '#059669' : '#d97706' }}>
                        {isCompleted ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InteractiveSandbox;
